import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSessionStatus } from '../../hooks/useSessionStatus';
import {
  completeDirectUpload,
  reprocessStuckSession,
  uploadExampleVideo,
  uploadFileToS3,
} from '../../api/beachvision';
import { DebugDrawer } from '../../components/beachvision/DebugDrawer';
import { DebugPanel } from '../../components/beachvision/DebugPanel';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';
import { SessionPipelineChecklist } from '../../components/beachvision/SessionPipelineChecklist';
import { isDebugModeEnabled } from '../../lib/debug-mode';
import {
  clearExampleVideoPending,
  clearPendingDirectUpload,
  clearSessionUploadMarker,
  getExampleVideoPendingForSession,
  getPendingForSession,
  markSessionUploadCompletedInStorage,
  patchExampleVideoPending,
  patchPendingDirectUpload,
  subscribeDirectUpload,
  isSessionUploadMarkedDone,
} from '../../utils/sessionDirectUpload';
import { videoProcessingDebugItems } from '../../utils/videoDebug';

const SERVER_DIRECT_UPLOAD_SNAPSHOT = Object.freeze({ pending: null, marked: false });

/** Fraction of the unified progress bar (0–1) used for the client → S3 upload phase. */
const UPLOAD_BAR_WEIGHT = 0.12;

/** Matches server `SessionReprocessView.MIN_PROCESSING_SECONDS_BEFORE_REPROCESS`. */
const STUCK_REPROCESS_MIN_SECONDS = 600;

const STUCK_REPROCESS_STORAGE_PREFIX = 'beachvision.stuck_reprocess_used.';

function stuckReprocessStorageKey(sessionId) {
  return `${STUCK_REPROCESS_STORAGE_PREFIX}${sessionId ?? ''}`;
}

function readStuckReprocessConsumed(sessionId) {
  if (typeof window === 'undefined' || sessionId == null || sessionId === '') {
    return false;
  }
  try {
    return Boolean(sessionStorage.getItem(stuckReprocessStorageKey(sessionId)));
  } catch {
    return false;
  }
}

function markStuckReprocessConsumed(sessionId) {
  if (typeof window === 'undefined' || sessionId == null || sessionId === '') {
    return;
  }
  try {
    sessionStorage.setItem(stuckReprocessStorageKey(sessionId), '1');
  } catch {
    /* private mode / quota */
  }
}

function formatRuntimeSeconds(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) {
    return '—';
  }
  const sec = Math.max(0, Number(seconds));
  const whole = Math.floor(sec);
  const m = Math.floor(whole / 60);
  const r = whole % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${mm}m ${r}s`;
  }
  if (m > 0) {
    return `${m}m ${r}s`;
  }
  if (sec < 60 && sec % 1 !== 0) {
    return `${sec.toFixed(1)}s`;
  }
  return `${whole}s`;
}

function serializeDirectUploadSnapshot(sessionId, pending, marked) {
  if (!pending) {
    return `${sessionId}|${marked ? 1 : 0}|`;
  }
  return `${sessionId}|${marked ? 1 : 0}|${pending.phase}|${pending.progress}|${pending.error ?? ''}`;
}

function useSessionDirectUploadStore(sessionId) {
  const snapshotCacheRef = useRef({ serialized: '', snapshot: SERVER_DIRECT_UPLOAD_SNAPSHOT });

  return useSyncExternalStore(
    subscribeDirectUpload,
    () => {
      const pending = getPendingForSession(sessionId);
      const marked = isSessionUploadMarkedDone(sessionId);
      const serialized = serializeDirectUploadSnapshot(sessionId, pending, marked);
      if (
        serialized === snapshotCacheRef.current.serialized &&
        snapshotCacheRef.current.snapshot
      ) {
        return snapshotCacheRef.current.snapshot;
      }
      const snapshot = { pending, marked };
      snapshotCacheRef.current = { serialized, snapshot };
      return snapshot;
    },
    () => SERVER_DIRECT_UPLOAD_SNAPSHOT
  );
}

const EXAMPLE_PENDING_SNAPSHOT = Object.freeze({ pending: null });

function serializeExamplePendingSnapshot(sessionId, ex) {
  if (!ex) {
    return `${sessionId}|`;
  }
  return `${sessionId}|${ex.phase}|${ex.error ?? ''}|${ex.exampleVideoName ?? ''}`;
}

function useExampleVideoPendingStore(sessionId) {
  const snapshotCacheRef = useRef({ serialized: '', snapshot: EXAMPLE_PENDING_SNAPSHOT });

  return useSyncExternalStore(
    subscribeDirectUpload,
    () => {
      const pending = getExampleVideoPendingForSession(sessionId);
      const serialized = serializeExamplePendingSnapshot(sessionId, pending);
      if (
        serialized === snapshotCacheRef.current.serialized &&
        snapshotCacheRef.current.snapshot
      ) {
        return snapshotCacheRef.current.snapshot;
      }
      const snapshot = { pending };
      snapshotCacheRef.current = { serialized, snapshot };
      return snapshot;
    },
    () => EXAMPLE_PENDING_SNAPSHOT
  );
}

/**
 * Waits for AI processing and routes to analysis or failure UI.
 */
function SessionDetailInner({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [stuckReprocessConsumed, setStuckReprocessConsumed] = useState(() =>
    readStuckReprocessConsumed(id),
  );

  useEffect(() => {
    setStuckReprocessConsumed(readStuckReprocessConsumed(id));
  }, [id]);

  const reprocessMutation = useMutation({
    mutationFn: () => reprocessStuckSession(id),
    onSuccess: () => {
      markStuckReprocessConsumed(id);
      setStuckReprocessConsumed(true);
      toast.success('Reprocessing queued');
      queryClient.invalidateQueries({ queryKey: ['beachvision', 'sessionStatus', id] });
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.error || err?.message || 'Could not reprocess';
      toast.error(msg);
    },
  });
  const { data, isError, isPending, refetch } = useSessionStatus(id, 3000);
  const { pending: directPending, marked: uploadMarkedDone } = useSessionDirectUploadStore(id);
  const { pending: exampleVideoPending } = useExampleVideoPendingStore(id);
  const redirected = useRef(false);
  const sawActiveProcessingState = useRef(false);
  const completeToastShown = useRef(false);

  const pointProgress = data?.point_processing;
  const totalPoints = pointProgress?.total ?? 0;
  const processedPoints = pointProgress?.processed ?? 0;
  const debugModeEnabled = isDebugModeEnabled(currentUser);
  const pipelineTiming = data?.pipeline_timing;
  const debugItems = useMemo(() => {
    const base = [
      ...(debugModeEnabled
        ? [
            { label: 'Session title', value: String(data?.title ?? '—') },
            {
              label: 'Video file',
              value: data?.video_filename ? String(data.video_filename) : '—',
            },
            {
              label: 'Session runtime',
              value: formatRuntimeSeconds(data?.session_runtime_seconds),
            },
            ...(data?.analysis_completed_at
              ? [
                  {
                    label: 'Analysis completed at',
                    value: String(data.analysis_completed_at),
                  },
                ]
              : []),
          ]
        : []),
      { label: 'Session ID', value: String(id || '-') },
      { label: 'Status', value: String(data?.status || '-') },
      { label: 'Stage', value: String(data?.stage || '-') },
      { label: 'Progress %', value: String(data?.progress_pct ?? '-') },
      {
        label: 'Point processing',
        value:
          pointProgress && totalPoints
            ? `${Math.min(processedPoints, totalPoints)}/${totalPoints}`
            : 'Not started',
      },
      { label: 'Failure code', value: String(data?.failure_code || 'none') },
    ];
    const videoRows =
      debugModeEnabled && pipelineTiming?.video_debug
        ? videoProcessingDebugItems(pipelineTiming.video_debug)
        : [];
    if (!debugModeEnabled) {
      return base;
    }
    const timing = pipelineTiming && typeof pipelineTiming === 'object' ? pipelineTiming : null;
    if (!timing) {
      return [...base, ...videoRows];
    }
    const orderedKeys = [
      'video_upload',
      'video_processing',
      'video_processing_s3_to_temp',
      'video_processing_ffmpeg_transcode',
      'video_processing_temp_to_cache',
      'video_processing_gemini_upload',
      'quality_check',
      'point_extraction',
      'point_analysis_max_seconds',
      'finalize_analysis',
      'extract_analysis_frames',
    ];
    const timingRows = [];
    for (const key of orderedKeys) {
      if (timing[key] != null) {
        timingRows.push({ label: `Timing ${key}`, value: `${timing[key]}s` });
      }
    }
    const stepInfo = timing.pipeline_step_info;
    if (stepInfo && typeof stepInfo === 'object') {
      const orderedStepInfoKeys = ['quality_check', 'point_extraction'];
      for (const step of orderedStepInfoKeys) {
        if (stepInfo[step]) {
          timingRows.push({
            label: `Model fallback ${step}`,
            value: stepInfo[step],
          });
        }
      }
    }
    if (
      timing.point_analysis_by_point &&
      typeof timing.point_analysis_by_point === 'object'
    ) {
      timingRows.push({
        label: 'Timing point_analysis_by_point',
        value: timing.point_analysis_by_point,
      });
    }
    return [...base, ...videoRows, ...timingRows];
  }, [
    data?.title,
    data?.video_filename,
    data?.session_runtime_seconds,
    data?.analysis_completed_at,
    data?.status,
    data?.stage,
    data?.progress_pct,
    data?.failure_code,
    debugModeEnabled,
    id,
    pipelineTiming,
    pointProgress,
    processedPoints,
    totalPoints,
  ]);

  useEffect(() => {
    redirected.current = false;
    completeToastShown.current = false;
    sawActiveProcessingState.current = false;
  }, [id]);

  useEffect(() => {
    if (data?.status === 'processing' || data?.status === 'pending') {
      sawActiveProcessingState.current = true;
    }
  }, [data?.status]);

  useEffect(() => {
    if (data?.status !== 'complete' || redirected.current) {
      return;
    }
    if (debugModeEnabled) {
      if (sawActiveProcessingState.current && !completeToastShown.current) {
        completeToastShown.current = true;
        toast.success('Analysis complete');
      }
      return;
    }
    redirected.current = true;
    if (sawActiveProcessingState.current && !completeToastShown.current) {
      completeToastShown.current = true;
      toast.success('Analysis complete');
    }
    const analysisId = data?.analysis_id || id;
    clearSessionUploadMarker(id);
    navigate(`/analysis/${analysisId}/takeaways/`, { replace: true });
  }, [data?.status, data?.analysis_id, debugModeEnabled, id, navigate]);

  useEffect(() => {
    const p = getPendingForSession(id);
    if (!p || p.phase !== 'pending') return;
    patchPendingDirectUpload({ phase: 'uploading', progress: 0 });

    (async () => {
      try {
        await uploadFileToS3(p.spec, p.file, (pct) => {
          patchPendingDirectUpload({ progress: pct });
        });
        patchPendingDirectUpload({ phase: 'completing', progress: 100 });
        await completeDirectUpload(p.sessionId, p.spec.storage_key);
        markSessionUploadCompletedInStorage(p.sessionId);
        clearPendingDirectUpload();
        await queryClient.invalidateQueries({ queryKey: ['beachvision', 'sessionStatus', id] });
        toast.success('Video received — analysis starting');
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Upload failed';
        patchPendingDirectUpload({
          phase: 'error',
          error: msg,
        });
        toast.error(msg);
      }
    })();
  }, [id, queryClient]);

  useEffect(() => {
    const ex = getExampleVideoPendingForSession(id);
    if (!ex || ex.phase !== 'pending') return;
    patchExampleVideoPending({ phase: 'running' });

    (async () => {
      try {
        await uploadExampleVideo(id, ex.exampleVideoName);
        clearExampleVideoPending();
        await queryClient.invalidateQueries({ queryKey: ['beachvision', 'sessionStatus', id] });
        toast.success('Example clip ready — analysis starting');
      } catch (err) {
        const msg =
          err?.response?.status === 404
            ? 'Example video is not available yet.'
            : err?.response?.data?.error || 'Could not start example analysis.';
        patchExampleVideoPending({ phase: 'error', error: msg });
        toast.error(msg);
      }
    })();
  }, [id, queryClient]);

  const uploadRow = useMemo(() => {
    if (directPending) {
      if (directPending.phase === 'error') {
        return { state: 'error', meta: directPending.error || 'Failed' };
      }
      if (directPending.phase === 'completing') {
        return { state: 'in_progress', meta: '100%' };
      }
      if (directPending.phase === 'uploading' || directPending.phase === 'pending') {
        return { state: 'in_progress', meta: `${directPending.progress}%` };
      }
    }
    if (uploadMarkedDone) {
      return { state: 'complete', meta: '100%' };
    }
    if (exampleVideoPending) {
      if (exampleVideoPending.phase === 'error') {
        return {
          state: 'error',
          meta: exampleVideoPending.error || 'Failed',
          label: 'Preparing example clip',
          variant: 'example_prep',
        };
      }
      return {
        state: 'in_progress',
        meta: null,
        label: 'Preparing example clip',
        variant: 'example_prep',
      };
    }
    return null;
  }, [
    directPending?.phase,
    directPending?.progress,
    directPending?.error,
    uploadMarkedDone,
    exampleVideoPending?.phase,
    exampleVideoPending?.error,
  ]);

  const serverProgressPct = Math.min(100, Math.max(0, data?.progress_pct ?? 0));

  const unifiedBarPct = useMemo(() => {
    if (!uploadRow) {
      return serverProgressPct;
    }
    if (uploadRow.state === 'error') {
      return 0;
    }
    if (uploadRow.state === 'in_progress') {
      if (uploadRow.variant === 'example_prep') {
        return UPLOAD_BAR_WEIGHT * 88;
      }
      const uploadPct =
        directPending?.phase === 'completing'
          ? 100
          : Math.max(0, directPending?.progress ?? 0);
      return UPLOAD_BAR_WEIGHT * uploadPct;
    }
    return (
      UPLOAD_BAR_WEIGHT * 100 + (1 - UPLOAD_BAR_WEIGHT) * serverProgressPct
    );
  }, [
    directPending?.phase,
    directPending?.progress,
    serverProgressPct,
    uploadRow,
  ]);

  const waitingOnClientWork = uploadRow?.state === 'in_progress';
  const videoUploadStepDone =
    uploadRow?.state === 'complete' ||
    (uploadRow == null && Boolean(data?.video_filename));
  const processingElapsed = data?.processing_elapsed_seconds;
  const showStuckReprocess =
    !stuckReprocessConsumed &&
    !waitingOnClientWork &&
    videoUploadStepDone &&
    (data?.status === 'pending' || data?.status === 'processing') &&
    typeof processingElapsed === 'number' &&
    processingElapsed >= STUCK_REPROCESS_MIN_SECONDS;

  if (isPending && !data && !directPending && !uploadMarkedDone && !exampleVideoPending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center">
        <p className="text-beachvision-muted text-sm">Loading session…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-4 py-20 text-center">
        <p className="text-beachvision-muted mb-4">Could not load status.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="bg-beachvision-coral text-white rounded-xl px-6 py-3 font-bold"
        >
          Retry
        </button>
      </div>
    );
  }

  if (data?.status === 'failed') {
    const rawFailureMessage =
      data?.failure_message || 'We could not finish processing this video. Try uploading again.';
    const hintSplit = String(rawFailureMessage).split('Hint:');
    const failureMessage = hintSplit[0]?.trim() || 'We could not finish processing this video.';
    const failureHint = hintSplit.length > 1 ? hintSplit.slice(1).join('Hint:').trim() : '';
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center">
        <div className="rounded-xl bg-beachvision-card p-8 shadow-lg max-w-sm w-full">
          <h2 className="text-white font-bold text-lg mb-2">Analysis failed</h2>
          <p className="text-beachvision-muted text-sm mb-3">
            {failureMessage}
          </p>
          {failureHint ? (
            <p className="text-beachvision-coral text-xs mb-6">Hint: {failureHint}</p>
          ) : (
            <div className="mb-6" />
          )}
          <button
            type="button"
            onClick={() => navigate('/upload/')}
            className="w-full bg-beachvision-coral text-white rounded-xl px-6 py-3 font-bold"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const analysisCompleteDebugHold =
    debugModeEnabled && data?.status === 'complete';

  return (
    <div className="flex flex-col items-center justify-start min-h-[70vh] px-6 pt-8 pb-10 text-center">
      <h2 className="text-white font-display font-bold text-lg mb-2">
        {analysisCompleteDebugHold ? 'Analysis complete' : 'Analyzing your match…'}
      </h2>
      <p className="text-beachvision-muted text-sm mb-6">
        {analysisCompleteDebugHold
          ? 'Review the debug timings below, then continue to your takeaways.'
          : 'This usually takes 1–3 minutes'}
      </p>
      <div className="w-full max-w-xs mb-6">
        <div className="h-3 rounded-full bg-beachvision-surface-low overflow-hidden">
          <motion.div
            className="h-full bg-beachvision-cta"
            initial={false}
            animate={{ width: `${Math.min(100, unifiedBarPct)}%` }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
        </div>
      </div>

      <SessionPipelineChecklist
        uploadRow={uploadRow}
        statusData={data}
        debugMode={debugModeEnabled}
        pipelineTiming={pipelineTiming}
      />

      {showStuckReprocess ? (
        <button
          type="button"
          disabled={reprocessMutation.isPending}
          className="mt-6 w-full max-w-sm rounded-xl border border-beachvision-muted/40 bg-beachvision-surface-low px-6 py-3 font-display font-bold text-white disabled:opacity-50"
          onClick={() => reprocessMutation.mutate()}
        >
          {reprocessMutation.isPending ? 'Queueing…' : 'Stuck - try to reprocess'}
        </button>
      ) : null}

      {directPending?.phase === 'error' || exampleVideoPending?.phase === 'error' ? (
        <button
          type="button"
          className="mt-6 w-full max-w-sm bg-beachvision-coral text-white rounded-xl px-6 py-3 font-bold"
          onClick={() => {
            clearExampleVideoPending();
            navigate('/upload/');
          }}
        >
          Try again
        </button>
      ) : null}

      {analysisCompleteDebugHold ? (
        <button
          type="button"
          className="mt-6 w-full max-w-sm rounded-xl bg-beachvision-cta px-6 py-3 font-display font-bold text-white"
          onClick={() => {
            if (redirected.current) return;
            redirected.current = true;
            const analysisId = data?.analysis_id || id;
            clearSessionUploadMarker(id);
            navigate(`/analysis/${analysisId}/takeaways/`, { replace: true });
          }}
        >
          Proceed
        </button>
      ) : null}

      <DebugDrawer enabled={debugModeEnabled}>
        <DebugPanel title="Admin Debug" items={debugItems} />
      </DebugDrawer>
    </div>
  );
}

export default function SessionDetailPage(props) {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <SessionDetailInner {...props} />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
